import React, { useRef, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

/* ══════════ CANVAS CLASSES ══════════ */

class NetNode {
  constructor(w, h) {
    this.x = Math.random() * w;
    this.y = Math.random() * h;
    this.vx = (Math.random() - 0.5) * 0.42;
    this.vy = (Math.random() - 0.5) * 0.42;
    this.r = 1.5 + Math.random() * 2.3;
    this.ph = Math.random() * Math.PI * 2;
    this.phSpd = 0.014 + Math.random() * 0.018;
    this.hue = 192 + Math.random() * 42;
    this.ba = 0.22 + Math.random() * 0.4;
  }
  update(w, h) {
    this.x += this.vx; this.y += this.vy; this.ph += this.phSpd;
    if (this.x < 0 || this.x > w) this.vx = -this.vx;
    if (this.y < 0 || this.y > h) this.vy = -this.vy;
  }
  draw(ctx) {
    const s = Math.sin(this.ph);
    ctx.save();
    ctx.globalAlpha = this.ba * (0.6 + 0.4 * s);
    ctx.shadowColor = `hsl(${this.hue},92%,72%)`;
    ctx.shadowBlur = 8;
    ctx.fillStyle = `hsl(${this.hue},92%,74%)`;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r * (1 + 0.2 * s), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawNet(ctx, nodes) {
  const maxD = 128;
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
      if (d < maxD) {
        ctx.save();
        ctx.globalAlpha = (1 - d / maxD) * 0.19;
        ctx.strokeStyle = "rgba(100,181,246,1)";
        ctx.lineWidth = 0.65;
        ctx.beginPath();
        ctx.moveTo(nodes[i].x, nodes[i].y);
        ctx.lineTo(nodes[j].x, nodes[j].y);
        ctx.stroke();
        ctx.restore();
      }
    }
  }
}

class TextParticle {
  constructor(w, h, tx, ty) {
    const ox = w * 0.52, oy = h * 0.33;
    this.x = ox + (Math.random() - 0.5) * w * 0.42;
    this.y = oy + (Math.random() - 0.5) * h * 0.26;
    this.tx = tx; this.ty = ty;
    const t = Math.random();
    const hue = t < 0.45 ? 280 - t * 2.22 * 70 : 210 - (t - 0.45) * 1.82 * 22;
    const lit = 65 + Math.random() * 30;
    this.color = `hsl(${hue},93%,${lit}%)`;
    this.glow = `hsla(${hue},93%,${lit}%,0.5)`;
    this.sz = Math.random() * 2.1 + 0.5;
    this.spd = 0.028 + Math.random() * 0.052;
    this.alpha = 0; this.aSpd = 0.006 + Math.random() * 0.013;
    this.delay = Math.random() * 58; this.frame = 0;
    this.osc = Math.random() * Math.PI * 2;
    this.oscS = 0.013 + Math.random() * 0.024;
    this.oscA = Math.random() * 1.3;
  }
  update() {
    if (++this.frame < this.delay) return;
    this.osc += this.oscS;
    this.x += (this.tx - this.x) * this.spd + Math.sin(this.osc) * this.oscA * 0.11;
    this.y += (this.ty - this.y) * this.spd + Math.cos(this.osc * 0.7) * this.oscA * 0.07;
    if (this.alpha < 1) this.alpha = Math.min(1, this.alpha + this.aSpd);
  }
  draw(ctx) {
    if (this.frame < this.delay) return;
    const prox = Math.max(0, 1 - Math.hypot(this.tx - this.x, this.ty - this.y) / 50);
    ctx.save();
    ctx.globalAlpha = this.alpha * (0.3 + prox * 0.7);
    ctx.shadowColor = this.glow;
    ctx.shadowBlur = 2 + prox * 13;
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.sz * (0.58 + prox * 0.62), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

class AmbientDot {
  constructor(w, h) {
    this.w = w; this.h = h;
    this.x = Math.random() * w;
    this.y = h * (0.15 + Math.random() * 0.85);
    this.vy = -(0.18 + Math.random() * 0.55);
    this.vx = (Math.random() - 0.5) * 0.28;
    this.sz = Math.random() * 1.35 + 0.22;
    this.col = `hsl(${185 + Math.random() * 62},88%,74%)`;
    this.alpha = Math.random() * 0.3; this.maxA = 0.08 + Math.random() * 0.28;
  }
  update() {
    this.x += this.vx; this.y += this.vy;
    if (this.alpha < this.maxA) this.alpha += 0.004;
    if (this.y < -8) { this.y = this.h + 4; this.x = Math.random() * this.w; this.alpha = 0; }
  }
  draw(ctx) {
    ctx.save(); ctx.globalAlpha = this.alpha;
    ctx.fillStyle = this.col;
    ctx.beginPath(); ctx.arc(this.x, this.y, this.sz, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
}

/* ══════════ ECG LINE ══════════ */
const ECGLine = () => {
  const d = "M0,30 L12,30 L17,30 L21,7 L25,53 L29,30 L40,30 L52,30 L56,30 L60,19 L64,41 L68,30 L78,30 L95,30 L100,30";
  return (
    <Box sx={{ position: "absolute", bottom: "22%", left: 0, right: 0, zIndex: 3, px: 2.5, opacity: 0.65 }}>
      <motion.svg viewBox="0 0 100 60" preserveAspectRatio="none" style={{ width: "100%", height: 38 }}>
        <defs>
          <linearGradient id="ecgG" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(100,181,246,0)"/>
            <stop offset="25%"  stopColor="rgba(100,181,246,0.85)"/>
            <stop offset="75%"  stopColor="rgba(206,147,216,0.85)"/>
            <stop offset="100%" stopColor="rgba(206,147,216,0)"/>
          </linearGradient>
        </defs>
        <motion.path d={d} stroke="url(#ecgG)" strokeWidth="4.5" fill="none" opacity={0.26}
          strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.26 }}
          transition={{ pathLength: { duration: 2.8, delay: 0.8, ease: "easeInOut" }, opacity: { duration: 0.4, delay: 0.8 } }}
        />
        <motion.path d={d} stroke="url(#ecgG)" strokeWidth="1.8" fill="none"
          strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
          transition={{ pathLength: { duration: 2.8, delay: 0.8, ease: "easeInOut" }, opacity: { duration: 0.4, delay: 0.8 } }}
        />
      </motion.svg>
    </Box>
  );
};

/* ══════════ FLOATING PILL ══════════ */
const Pill = ({ style, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.6 }}
    animate={{ opacity: 1, scale: 1, y: [0, -10, 0], rotate: [0, 4, -4, 0] }}
    transition={{
      opacity: { duration: 0.6, delay },
      scale: { duration: 0.6, delay },
      y: { duration: 4.5, delay, repeat: Infinity, ease: "easeInOut" },
      rotate: { duration: 6, delay, repeat: Infinity, ease: "easeInOut" },
    }}
    style={{ position: "absolute", zIndex: 3, pointerEvents: "none", ...style }}
  >
    <svg viewBox="0 0 52 24" width="46"
      style={{ display: "block", filter: "drop-shadow(0 0 6px rgba(100,181,246,0.55))" }}>
      <rect x="1" y="1" width="50" height="22" rx="11"
        fill="rgba(255,255,255,0.06)" stroke="rgba(100,181,246,0.48)" strokeWidth="1.3"/>
      <line x1="26" y1="1" x2="26" y2="23" stroke="rgba(100,181,246,0.38)" strokeWidth="1.1"/>
      <rect x="1" y="1" width="25" height="22" rx="11" fill="rgba(100,181,246,0.15)"/>
      <ellipse cx="13" cy="12" rx="4.5" ry="7" fill="rgba(255,255,255,0.07)" transform="rotate(-22,13,12)"/>
    </svg>
  </motion.div>
);

/* ══════════ FLOATING MOLECULE ══════════ */
const Molecule = ({ style, delay = 0, sz = 32 }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: [0, 0.6, 0.6], y: [0, -9, 0], rotate: [0, 360] }}
    transition={{
      opacity: { duration: 0.9, delay },
      y: { duration: 5, delay, repeat: Infinity, ease: "easeInOut" },
      rotate: { duration: 14, delay, repeat: Infinity, ease: "linear" },
    }}
    style={{ position: "absolute", zIndex: 2, pointerEvents: "none", ...style }}
  >
    <svg viewBox="0 0 60 60" width={sz}
      style={{ filter: "drop-shadow(0 0 4px rgba(100,181,246,0.4))" }}>
      <circle cx="30" cy="30" r="5.2" fill="rgba(100,181,246,0.72)"/>
      <ellipse cx="30" cy="30" rx="24" ry="9" fill="none" stroke="rgba(100,181,246,0.28)" strokeWidth="1.1"/>
      <ellipse cx="30" cy="30" rx="24" ry="9" fill="none" stroke="rgba(100,181,246,0.28)" strokeWidth="1.1" transform="rotate(60,30,30)"/>
      <ellipse cx="30" cy="30" rx="24" ry="9" fill="none" stroke="rgba(100,181,246,0.28)" strokeWidth="1.1" transform="rotate(-60,30,30)"/>
      <circle cx="30" cy="6"  r="2.8" fill="rgba(206,147,216,0.65)"/>
      <circle cx="51" cy="30" r="2.8" fill="rgba(206,147,216,0.65)"/>
      <circle cx="30" cy="54" r="2.8" fill="rgba(206,147,216,0.65)"/>
    </svg>
  </motion.div>
);

/* ══════════ SCAN LINE ══════════ */
const ScanLine = () => (
  <motion.div
    animate={{ top: ["0%", "103%"] }}
    transition={{ duration: 5.5, repeat: Infinity, ease: "linear", repeatDelay: 2.5 }}
    style={{
      position: "absolute", left: 0, right: 0, height: 2,
      background: "linear-gradient(90deg,transparent,rgba(100,181,246,0.22),rgba(206,147,216,0.2),transparent)",
      zIndex: 8, pointerEvents: "none",
    }}
  />
);

/* ══════════ DOCTOR SVG ══════════ */
const DoctorSVG = () => (
  <svg viewBox="0 0 280 640" style={{ width: "100%", height: "100%", display: "block" }} fill="none">
    <defs>
      <filter id="dGS" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="5" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
      <filter id="dGM" x="-100%" y="-100%" width="300%" height="300%">
        <feGaussianBlur stdDeviation="12" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
      <linearGradient id="dCoat" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(255,255,255,0.97)"/>
        <stop offset="100%" stopColor="rgba(205,220,252,0.84)"/>
      </linearGradient>
      <radialGradient id="dFace" cx="44%" cy="36%">
        <stop offset="0%" stopColor="#ffe0cf"/>
        <stop offset="100%" stopColor="#ffb08a"/>
      </radialGradient>
      <radialGradient id="dGlove" cx="32%" cy="28%">
        <stop offset="0%" stopColor="#42a5f5"/>
        <stop offset="100%" stopColor="#0d47a1"/>
      </radialGradient>
      <radialGradient id="dBG" cx="50%" cy="50%">
        <stop offset="0%" stopColor="rgba(100,181,246,0.09)"/>
        <stop offset="100%" stopColor="transparent"/>
      </radialGradient>
    </defs>

    <ellipse cx="140" cy="380" rx="100" ry="285" fill="url(#dBG)" filter="url(#dGM)"/>
    <path d="M68 200 L212 200 L238 610 H42 Z" fill="url(#dCoat)" filter="url(#dGS)"/>
    <path d="M140 200 L94 260 L116 285 L140 248 Z" fill="rgba(255,255,255,0.97)"/>
    <path d="M140 200 L186 260 L164 285 L140 248 Z" fill="rgba(255,255,255,0.97)"/>
    <path d="M116 200 L164 200 L156 318 H124 Z" fill="rgba(21,101,192,0.70)"/>
    <path d="M68 215 Q24 335 28 440 L60 438 Q66 330 92 225 Z" fill="url(#dCoat)"/>
    <path d="M212 215 Q252 282 258 368 L228 373 Q224 288 188 225 Z" fill="url(#dCoat)"/>
    <ellipse cx="140" cy="112" rx="52" ry="60" fill="url(#dFace)" filter="url(#dGS)"/>
    <path d="M88 100 Q94 52 140 46 Q186 52 192 100 Q180 68 140 64 Q100 68 88 100 Z" fill="#3b2215"/>
    <path d="M88 100 Q80 120 86 144" stroke="#3b2215" strokeWidth="9" fill="none" strokeLinecap="round"/>
    <path d="M192 100 Q200 120 194 144" stroke="#3b2215" strokeWidth="9" fill="none" strokeLinecap="round"/>
    <rect x="120" y="168" width="40" height="36" rx="8" fill="url(#dFace)"/>
    <ellipse cx="120" cy="107" rx="7.5" ry="6.5" fill="#2c1510"/>
    <ellipse cx="160" cy="107" rx="7.5" ry="6.5" fill="#2c1510"/>
    <ellipse cx="122" cy="105" rx="2.8" ry="2.2" fill="white" opacity="0.88"/>
    <ellipse cx="162" cy="105" rx="2.8" ry="2.2" fill="white" opacity="0.88"/>
    <path d="M110 97 Q119 92 130 96"  stroke="#3b2215" strokeWidth="3.2" fill="none" strokeLinecap="round"/>
    <path d="M150 96 Q161 92 170 97"  stroke="#3b2215" strokeWidth="3.2" fill="none" strokeLinecap="round"/>
    <path d="M135 120 Q140 131 145 120" stroke="rgba(150,75,55,0.42)" strokeWidth="1.9" fill="none"/>
    <path d="M127 141 Q140 150 153 141" stroke="rgba(165,72,72,0.62)" strokeWidth="2.4" fill="none" strokeLinecap="round"/>
    <ellipse cx="30"  cy="452" rx="24" ry="34" fill="url(#dGlove)" filter="url(#dGS)"/>
    <rect x="10"  y="423" width="12" height="30" rx="6" fill="url(#dGlove)"/>
    <rect x="24"  y="419" width="12" height="34" rx="6" fill="url(#dGlove)"/>
    <rect x="38"  y="422" width="12" height="30" rx="6" fill="url(#dGlove)"/>
    <ellipse cx="8" cy="458" rx="9" ry="15" fill="url(#dGlove)" transform="rotate(-23,8,458)"/>
    <g transform="translate(236,362) rotate(24)">
      <ellipse cx="0" cy="0" rx="24" ry="34" fill="url(#dGlove)" filter="url(#dGS)"/>
      <rect x="-23" y="-27" width="12" height="29" rx="6" fill="url(#dGlove)"/>
      <rect x="-9"  y="-31" width="12" height="33" rx="6" fill="url(#dGlove)"/>
      <rect x="5"   y="-31" width="12" height="33" rx="6" fill="url(#dGlove)"/>
      <rect x="19"  y="-28" width="12" height="30" rx="6" fill="url(#dGlove)"/>
      <ellipse cx="-27" cy="7" rx="9" ry="15" fill="url(#dGlove)" transform="rotate(30,-27,7)"/>
    </g>
    <path d="M116 285 Q92 326 98 374 Q104 402 128 408 Q156 413 164 384 Q172 356 165 320 L157 285"
      stroke="rgba(100,181,246,0.5)" strokeWidth="4.2" fill="none" strokeLinecap="round"/>
    <circle cx="128" cy="411" r="12" fill="none" stroke="rgba(100,181,246,0.5)" strokeWidth="3"/>
    <rect x="165" y="332" width="36" height="44" rx="5" fill="none" stroke="rgba(175,205,230,0.36)" strokeWidth="1.6"/>
    <line x1="175" y1="332" x2="173" y2="371" stroke="rgba(100,181,246,0.52)" strokeWidth="2.3" strokeLinecap="round"/>
    <line x1="186" y1="332" x2="184" y2="371" stroke="rgba(100,181,246,0.52)" strokeWidth="2.3" strokeLinecap="round"/>
    <path d="M42 610 L238 610" stroke="rgba(180,210,240,0.18)" strokeWidth="1.5"/>
  </svg>
);

/* ══════════ CAPSULE + BURST ══════════ */
const CapsuleBurst = ({ burst }) => (
  <Box sx={{ position: "absolute", top: "31%", left: "62%", transform: "translate(-50%,-50%)", zIndex: 4, pointerEvents: "none" }}>
    <AnimatePresence>
      {burst && [0, 0.12, 0.28].map((delay, i) => (
        <motion.div key={i}
          initial={{ scale: 0, opacity: 0.72 }} animate={{ scale: 5 + i, opacity: 0 }} exit={{}}
          transition={{ duration: 1.4, delay, ease: "easeOut" }}
          style={{ position: "absolute", width: 90, height: 90, borderRadius: "50%",
            background: "radial-gradient(circle,rgba(100,181,246,0.52),transparent 70%)",
            top: "50%", left: "50%", marginTop: -45, marginLeft: -45 }}
        />
      ))}
    </AnimatePresence>
    <AnimatePresence>
      {burst && Array.from({ length: 20 }).map((_, i) => {
        const angle = (i / 20) * Math.PI * 2;
        const dist = 58 + Math.random() * 52;
        return (
          <motion.div key={`t${i}`}
            initial={{ x: 0, y: 0, opacity: 0.95, scale: 1 }}
            animate={{ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, opacity: 0, scale: 0.22 }}
            exit={{}}
            transition={{ duration: 0.9 + Math.random() * 0.4, delay: 0.04, ease: "easeOut" }}
            style={{ position: "absolute", borderRadius: "50%",
              width: 4 + Math.random() * 7, height: 4 + Math.random() * 7,
              background: `hsl(${188 + i * 9},95%,72%)`,
              boxShadow: `0 0 9px hsl(${188 + i * 9},95%,72%)`,
              top: "50%", left: "50%", marginTop: -3, marginLeft: -3 }}
          />
        );
      })}
    </AnimatePresence>
    <motion.div
      animate={burst
        ? { scale: [1, 1.6, 0.1], opacity: [1, 1, 0], rotate: [0, -18, 38] }
        : { scale: [1, 1.06, 1], opacity: 1 }}
      transition={burst
        ? { duration: 0.55, ease: "easeIn" }
        : { duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg viewBox="0 0 60 132" width={58}
        style={{ filter: "drop-shadow(0 0 22px rgba(100,181,246,0.9)) drop-shadow(0 0 7px rgba(255,255,255,0.55))", display: "block" }}>
        <defs>
          <linearGradient id="cTop" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(66,165,245,0.9)"/>
            <stop offset="100%" stopColor="rgba(100,181,246,0.65)"/>
          </linearGradient>
          <linearGradient id="cBot" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(255,255,255,0.85)"/>
            <stop offset="100%" stopColor="rgba(215,235,255,0.58)"/>
          </linearGradient>
        </defs>
        <path d="M8 28 Q8 5 30 5 Q52 5 52 28 L52 104 Q52 127 30 127 Q8 127 8 104 Z"
          fill="rgba(100,181,246,0.07)" stroke="rgba(255,255,255,0.56)" strokeWidth="1.6"/>
        <path d="M8 66 L8 28 Q8 5 30 5 Q52 5 52 28 L52 66 Z" fill="url(#cTop)"/>
        <path d="M8 66 L8 104 Q8 127 30 127 Q52 127 52 104 L52 66 Z" fill="url(#cBot)"/>
        <line x1="5" y1="66" x2="55" y2="66" stroke="rgba(255,255,255,0.52)" strokeWidth="1.5"/>
        <ellipse cx="18" cy="38" rx="5.5" ry="17" fill="rgba(255,255,255,0.38)" transform="rotate(-12,18,38)"/>
        <ellipse cx="18" cy="98" rx="4"   ry="10" fill="rgba(255,255,255,0.18)" transform="rotate(-12,18,98)"/>
      </svg>
    </motion.div>
  </Box>
);

/* ══════════ MAIN COMPONENT ══════════ */
const LoginHero = () => {
  const canvasRef    = useRef(null);
  const rafRef       = useRef(null);
  const activeRef    = useRef(false);
  const particlesRef = useRef([]);
  const ambientRef   = useRef([]);
  const nodesRef     = useRef([]);

  const [burst,       setBurst]       = useState(false);
  const [showDoctor,  setShowDoctor]  = useState(false);
  const [showTagline, setShowTagline] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setShowDoctor(true),       120);
    const t2 = setTimeout(() => setBurst(true),            1700);
    const t3 = setTimeout(() => { activeRef.current = true; }, 2200);
    const t4 = setTimeout(() => setShowTagline(true),      5500);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const setup = () => {
      canvas.width  = canvas.offsetWidth  || 640;
      canvas.height = canvas.offsetHeight || 720;
      build(canvas.width, canvas.height);
    };

    const build = (w, h) => {
      nodesRef.current = Array.from({ length: 22 }, () => new NetNode(w, h));

      ambientRef.current = Array.from({ length: 95 }, () => {
        const d = new AmbientDot(w, h);
        d.y = Math.random() * h;
        return d;
      });

      const tmp = Object.assign(document.createElement("canvas"), { width: w, height: h });
      const tctx = tmp.getContext("2d");
      const fs = Math.min(w * 0.176, 108, h * 0.154);
      tctx.font = `900 ${fs}px "Inter","Helvetica Neue",sans-serif`;
      tctx.textAlign = "center";
      tctx.textBaseline = "middle";
      tctx.fillStyle = "white";
      tctx.fillText("NAARI", w / 2, h * 0.76);

      const id = tctx.getImageData(0, 0, w, h).data;
      const pts = [];
      const gap = Math.max(3, Math.floor(w / 192));
      for (let y = 0; y < h; y += gap)
        for (let x = 0; x < w; x += gap)
          if (id[(y * w + x) * 4 + 3] > 110) pts.push({ x, y });

      const cap = Math.min(pts.length, 2200);
      particlesRef.current = Array.from({ length: cap }, (_, i) => {
        const p = pts[Math.floor(i * pts.length / cap)];
        return new TextParticle(w, h, p.x, p.y);
      });
    };

    setup();
    const ro = new ResizeObserver(setup);
    ro.observe(canvas);

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodesRef.current.forEach(n => { n.update(canvas.width, canvas.height); n.draw(ctx); });
      drawNet(ctx, nodesRef.current);
      ambientRef.current.forEach(d => { d.update(); d.draw(ctx); });
      if (activeRef.current) particlesRef.current.forEach(p => { p.update(); p.draw(ctx); });
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();

    return () => { ro.disconnect(); cancelAnimationFrame(rafRef.current); };
  }, []);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>

      {/* Volumetric light */}
      <Box sx={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
        background: [
          "radial-gradient(ellipse 65% 52% at 62% 28%, rgba(100,181,246,0.16) 0%, transparent 68%)",
          "radial-gradient(ellipse 50% 65% at 22% 82%, rgba(156,39,176,0.08) 0%, transparent 68%)",
        ].join(","),
      }}/>

      {/* Hex grid overlay */}
      <Box sx={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none", opacity: 0.042,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100'%3E%3Cpath d='M28 66L0 50V18L28 2l28 16v32z' fill='none' stroke='%2364b5f6' stroke-width='1'/%3E%3C/svg%3E")`,
        backgroundSize: "56px 100px",
      }}/>

      {/* Canvas — network + ambient + NAARI */}
      <canvas ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", zIndex: 1 }}
      />

      {/* Scan line */}
      <ScanLine />

      {/* Doctor */}
      <AnimatePresence>
        {showDoctor && (
          <motion.div
            initial={{ opacity: 0, x: -32, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: "absolute", left: "4%", bottom: 0, width: "50%", maxWidth: 230, height: "90%", zIndex: 2 }}
          >
            <DoctorSVG />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Capsule */}
      <CapsuleBurst burst={burst} />

      {/* ECG */}
      <ECGLine />

      {/* Floating pills */}
      <Pill style={{ top: "16%", left: "54%", transform: "rotate(28deg)"  }} delay={1.2} />
      <Pill style={{ top: "48%", left: "74%", transform: "rotate(-18deg)" }} delay={2.0} />
      <Pill style={{ top: "58%", left: "62%", transform: "rotate(52deg)"  }} delay={1.6} />

      {/* Floating molecules */}
      <Molecule style={{ top: "10%", left: "68%" }} delay={0.8} sz={34} />
      <Molecule style={{ top: "36%", left: "80%" }} delay={1.8} sz={26} />

      {/* NAARI glow underlay */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 5, duration: 2.2 }}
        style={{
          position: "absolute", bottom: "14%", left: "50%", transform: "translateX(-50%)",
          width: "75%", height: 72, borderRadius: "50%",
          background: "radial-gradient(ellipse,rgba(100,181,246,0.24) 0%,transparent 70%)",
          filter: "blur(15px)", zIndex: 0, pointerEvents: "none",
        }}
      />

      {/* Tagline */}
      <AnimatePresence>
        {showTagline && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            style={{ position: "absolute", bottom: "7%", left: "50%", transform: "translateX(-50%)",
              zIndex: 5, textAlign: "center", width: "80%", pointerEvents: "none" }}
          >
            <Typography sx={{ color: "rgba(255,255,255,0.38)", fontSize: 11,
              letterSpacing: 3.5, textTransform: "uppercase", fontWeight: 500 }}>
              Field Force Tracker
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Jagsonpal badge */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        style={{ position: "absolute", top: 28, left: 28, zIndex: 6 }}
      >
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: 1,
          px: 1.8, py: 0.9, borderRadius: "100px",
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.10)",
          backdropFilter: "blur(8px)",
        }}>
          <Box sx={{
            width: 22, height: 22, borderRadius: "6px",
            background: "linear-gradient(135deg,#9c27b0,#e91e63)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 11, lineHeight: 1 }}>N</Typography>
          </Box>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 11.5, fontWeight: 500, letterSpacing: 0.4 }}>
            Jagsonpal Pharmaceuticals
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
};

export default LoginHero;
